// ============================================================
// EXPOSURE EFFECTS - the weapon side: an exposure a hit INFLICTS
// (v8.352.0). Parallel to afflictions (engine/afflictions.ts) and
// deliberately a SEPARATE spec (ruling 2026-09-12): an affliction
// affects the one hit, an exposure affects everyone who then
// interacts with them.
//
// Its own module because engine/exposures.ts is a LEAF (the grammar,
// the presets, the readers) that conditions.ts decodes and colours
// through - and this side needs conditions.ts back. Pure.
// ============================================================
import type { AfflictionTestId, AfflictionDurationMode } from './afflictions';
import { effectReaches, sideKey, type EffectAffects } from './effectScope';
import type { StackPolicy } from './stackPolicy';
import { afflictionTriggered } from './afflictions';
import type { WeaponEffectContext, WeaponEffectResult, WeaponEffectDetail } from './weaponEffects';
import { rollDiceExpression } from './dice';
import { mergeCondition } from './conditions';
import { resistTest, describeResist, type Vector } from './resistances';
import { hasWS } from './weaponSpecials';
import { parseExposure, buildExposureCode, exposureLabel, type Exposure } from './exposures';

export interface ExposureSpec {
  /** Display name - the log heading. */
  name: string;
  trigger: 'onHit' | 'onWound' | 'onCrit';
  /** Absent/null = no resist; the exposure simply lands. */
  test?: { characteristic: AfflictionTestId; modifier: number; vector?: Vector } | null;
  /** The `Ex:` code WITHOUT a duration - the fields below are the clock. */
  code: string;
  durationMode: AfflictionDurationMode;
  rounds?: number;
  dice?: string;
  /** Who it reaches, by the attacker's team (v8.367.4). Absent = anyone it hits. */
  affects?: EffectAffects;
  /** What it does to the same exposure already worn (v8.367.4). Absent = the session's ruling. */
  stacking?: StackPolicy;
}

const EXPOSURE_TEST_MAP: Record<AfflictionTestId, 'toughness' | 'dodge' | 'strength' | 'willpower'> = {
  toughness: 'toughness',
  agility: 'dodge',
  strength: 'strength',
  willpower: 'willpower',
};
const EXPOSURE_TEST_LABEL: Record<AfflictionTestId, string> = {
  toughness: 'Toughness',
  agility: 'Agility',
  strength: 'Strength',
  willpower: 'Willpower',
};

/** A spec's rounds for this landing: permanent → 0; dof- or dos-scaled; dice-rolled (one draw, appended). */
export function exposureRounds(spec: ExposureSpec, dof: number, dos = 0): number {
  switch (spec.durationMode) {
    case 'permanent':
      return 0;
    case 'rounds':
      return Math.max(0, spec.rounds ?? 0);
    case 'dof':
      return Math.max(1, dof);
    case 'dos':
      return Math.max(1, dos);
    case 'dice':
      return Math.max(1, rollDiceExpression(spec.dice || '1d5').total);
  }
}

const ICON = '◎';

/** Log one line: the narrative note and its detail row carry the same text. */
function report(result: WeaponEffectResult, detail: WeaponEffectDetail): void {
  result.narrativeNotes.push(detail.result);
  result.effectDetails.push(detail);
}

/**
 * The victim's resist, when the spec offers one (and the weapon is not
 * Hexed): the degrees of failure it lands with, or null when resisted.
 * No resist lands with one.
 */
function resistExposure(spec: ExposureSpec, ctx: WeaponEffectContext, result: WeaponEffectResult): number | null {
  if (!spec.test || hasWS(ctx.specialRules, 'Hexed')) return 1;
  const out = resistTest(
    ctx.target as unknown as Record<string, unknown>,
    spec.test.vector ?? '',
    EXPOSURE_TEST_MAP[spec.test.characteristic],
    0,
    spec.test.modifier,
    { ignoreImmunity: hasWS(ctx.specialRules, 'Purgatus') },
  );
  const line =
    ICON + ' ' + spec.name + ': ' + describeResist(out, EXPOSURE_TEST_LABEL[spec.test.characteristic] + ' test');
  const detail: WeaponEffectDetail = { name: spec.name, icon: ICON, success: out.passed, result: line };
  if (out.test) {
    detail.roll = out.test.roll;
    detail.target = out.test.target;
  }
  report(result, detail);
  return out.passed ? null : Math.max(1, out.test?.dof ?? 1);
}

/** Lay the exposure on the victim with its clock - rolling its dice if it has them. */
function applyExposure(
  spec: ExposureSpec,
  parsed: Exposure,
  ctx: WeaponEffectContext,
  dof: number,
  result: WeaponEffectResult,
): void {
  const rounds = exposureRounds(spec, dof, ctx.dos);
  const code = buildExposureCode({ ...parsed, rounds });
  mergeCondition(ctx.newConds, code, spec.stacking ?? ctx.stacking);
  const clock = rounds > 0 ? ' for ' + rounds + (rounds === 1 ? ' round' : ' rounds') : '';
  const text = ICON + ' ' + spec.name + ': ' + exposureLabel(parsed) + clock + '.';
  report(result, { name: spec.name, icon: ICON, result: text, conditionApplied: code });
}

/** True when the spec's trigger fired and it reaches this victim. */
function exposureFires(spec: ExposureSpec, ctx: WeaponEffectContext): boolean {
  if (!afflictionTriggered({ name: spec.name, trigger: spec.trigger, effects: [] }, ctx)) return false;
  return effectReaches(
    spec.affects,
    sideKey(ctx.attackerSideTag, ctx.attackerTeamTag),
    sideKey(ctx.target.sideTag, ctx.target.teamTag),
  );
}

/** One spec: parse its code, and if it fires and is not resisted, lay it. */
function processSpec(spec: ExposureSpec, ctx: WeaponEffectContext, result: WeaponEffectResult): void {
  const parsed = parseExposure(spec.code);
  if (!parsed || !exposureFires(spec, ctx)) return;
  const dof = resistExposure(spec, ctx, result);
  if (dof !== null) applyExposure(spec, parsed, ctx, dof, result);
}

/**
 * Resolve a weapon's exposures against the victim - AFTER the weapon
 * specials and the afflictions, so its dice append to the seeded
 * sequence. Per spec: the resist, then the duration dice. The exposure
 * is merged into `ctx.newConds` like any code; the same preset refreshes
 * rather than stacking (mergeCondition's rule for timed codes).
 */
export function processExposures(ctx: WeaponEffectContext): WeaponEffectResult {
  const result: WeaponEffectResult = { narrativeNotes: [], extraDamage: 0, effectDetails: [] };
  const specs = (ctx.slot as { exposures?: ExposureSpec[] }).exposures;
  if (!specs?.length || ctx.targetKilled) return result;
  for (const spec of specs) processSpec(spec, ctx, result);
  return result;
}
